import React, { Fragment, startTransition, useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { get, map } from 'lodash';

import { useToggle } from 'site-modules/shared/hooks/use-toggle';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { ComparatorModel } from 'client/data/models/comparator';
import { ConsumerReviewsEntities } from 'client/data/models/consumer-reviews';
import { VehicleEntities } from 'client/data/models/vehicle-v2';
import { EditorialReviewEntities } from 'client/data/models/editorial-review';
import { featuresMap, tcoMap } from 'client/data/models/car-features-map';
import {
  getColumnIdsString,
  getDisplayPriceValue,
  getTCOIdsString,
  hasFeatureValue,
} from 'site-modules/comparator/utils/comparator';
import { formatPriceString, getPriceString } from 'site-modules/shared/utils/price-utils';
import { getOverallRating, getRatingScale } from 'site-modules/shared/utils/editorial-helper';
import { getDealsUrl } from 'site-modules/shared/utils/core-link-constructor';
import { isUsed } from 'site-modules/shared/utils/inventory-utils/is-used';
import { numberWithCommasForString } from 'site-modules/shared/utils/string';
import { withHash } from 'site-modules/shared/utils/comparator/comparator-hash';
import { SUB_NAV_SECTIONS_MAP } from 'site-modules/comparator/constants/sections-map';
import { INCENTIVE_TYPES } from 'site-modules/shared/constants/incentives';

// Components
import { EdmundsRatingButton } from 'site-modules/shared/components/comparator/buttons/edmunds-rating-button/edmunds-rating-button';
import {
  AVAILABLE_ON_OTHER_STYLES,
  FeatureField,
} from 'site-modules/comparator/components/feature-field/feature-field';
import { OverallRating } from 'site-modules/shared/components/comparator/overall-rating/overall-rating';
import { RatingLines } from 'site-modules/comparator/components/rating-lines/rating-lines';
import { ConsumerRatingButton } from 'site-modules/comparator/components/buttons/consumer-rating-button/consumer-rating-button';
import { CarColorSection } from 'site-modules/shared/components/comparator/car-color-section/car-color-section';
import { ContentTable } from 'site-modules/shared/components/comparator/content-table/content-table';
import { ComparatorIncentivesAd } from 'site-modules/shared/components/native-ad/comparator-incentives-ad/comparator-incentives-ad';
import { AspectRatioContainer } from 'site-modules/shared/components/aspect-ratio-container/aspect-ratio-container';
import { CarImage } from 'site-modules/shared/components/comparator/car-image/car-image';
import { TypeRankingsLink } from 'site-modules/comparator/components/type-rankings-link/type-rankings-link';
import { CoreButton } from 'site-modules/comparator/components/buttons/core-button/core-button';
import { SrpButton } from 'site-modules/comparator/components/buttons/srp-button/srp-button';
import { CarName } from 'site-modules/shared/components/comparator/car-name/car-name';
import { ChangeButton } from 'site-modules/comparator/components/buttons/change-button/change-button';
import { AddButton } from 'site-modules/comparator/components/buttons/add-button/add-button';
import { LeadFormButton } from 'site-modules/comparator/components/buttons/lead-form-button/lead-form-button';
import { StyleDataWrapper } from 'site-modules/comparator/components/style-data-wrapper/style-data-wrapper';
import { CarSelectYmmsModal } from 'site-modules/comparator/components/car-select-ymms-modal/car-select-ymms-modal';
import { AnchorSubNavigation } from 'site-modules/shared/components/anchor-sub-navigation/anchor-sub-navigation';
import { AnimatedSticky } from 'site-modules/shared/components/animated-sticky/animated-sticky';
import { EditorialReviewRating } from 'site-modules/shared/components/comparator/editorial-review-rating/editorial-review-rating';
import { Link } from 'site-modules/shared/components/link/link';
import { Experiment, Recipe } from 'site-modules/shared/components/experiment';
import { ExperimentUtil } from 'client/utils/experiment/experiment-util';

import './comparator-table.scss';

const NOT_AVAILABLE = 'Not Available';

const getFeatureColumns = ({ evComparableFeatures, columns, feature }) =>
  columns.map((column, index) => ({
    column,
    feature: {
      ...feature,
      ...(get(evComparableFeatures, `[${index}].isElectric`) ? feature.electric : null),
      ...(get(evComparableFeatures, `[${index}].isPluginElectric`) ? feature.hybrid : null),
    },
  }));

function ComparatorTableUI({
  title,
  styles,
  columns,
  navList,
  addVehicle,
  removeVehicle,
  onStickyStateChange,
  evComparableFeatures,
  evTaxCredit,
  pricing,
  dealerCount,
  editorialReview,
  consumerRating,
  tco,
  colors,
  shouldRenderSelected,
  comparatorHeaderRef,
  comparatorHeaderStickyRef,
  customOffset,
  isSmall,
  hasComparisonContent,
  comparableFeatures,
  hideSimilar,
  enableAds10208,
  ads11245Recipe,
}) {
  const [modalStyleId, setModalStyleId] = useState(null);
  const [modalColumnName, setModalColumnName] = useState(null);
  const [isMMYSModalOpened, toggleMMYSModalOpened] = useToggle();

  const addVehicleHandler = useCallback(
    styleId => {
      addVehicle({
        styleId,
        name: modalColumnName,
      });
    },
    [addVehicle, modalColumnName]
  );

  const toggleModal = useCallback((styleId, columnName) => {
    setModalStyleId(columnName && styleId ? styleId.toString() : null);
    setModalColumnName(columnName);
    startTransition(() => {
      toggleMMYSModalOpened();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mmysModal = (
    <CarSelectYmmsModal
      toggleModal={toggleModal}
      isModalOpened={isMMYSModalOpened}
      isEmpty={!modalStyleId}
      addVehicle={addVehicleHandler}
    />
  );

  const contentData = useMemo(() => {
    const hasEvSection =
      !!evComparableFeatures &&
      evComparableFeatures.some(
        evComparableFeature => get(evComparableFeature, 'isElectric') || get(evComparableFeature, 'isPluginElectric')
      );

    const carHeaderTitleRenderMap = [
      {
        isThead: true,
        features: [
          {
            srOnly: true,
            data: columns.map(({ styleId }, index) => {
              const year = get(styles, `[${index}].year`);
              const makeName = get(styles, `[${index}].makeName`);
              const modelName = get(styles, `[${index}].modelName`);
              const styleName = get(styles, `[${index}].name`);

              return (
                <Fragment>{styleId ? `${year} ${makeName} ${modelName} ${styleName}` : 'No selected vehicle'}</Fragment>
              );
            }),
          },
        ],
      },
    ];

    const carHeaderRenderMap = [
      {
        ref: comparatorHeaderRef,
        alternateFeaturesBgColor: false,
        features: [
          {
            rowClassName: 'car-action-row',
            data: columns.map(({ styleId, name }, index) => {
              const isFirstColumn = index === 0;
              const isPreviousColumnEmpty = !!columns[index - 1] && !columns[index - 1].styleId;
              const isColumnDisabled = !isFirstColumn && isPreviousColumnEmpty;

              return styleId ? (
                <div className="d-flex flex-column justify-content-between h-100 pt-md-0_5">
                  <CarName
                    makeSlug={get(styles, `[${index}].makeSlug`)}
                    modelSlug={get(styles, `[${index}].modelSlug`)}
                    makeName={get(styles, `[${index}].makeName`)}
                    modelName={get(styles, `[${index}].modelName`)}
                    submodelSlug={get(styles, `[${index}].submodels.slug`)}
                    year={get(styles, `[${index}].year`)}
                    styleName={get(styles, `[${index}].name`)}
                    removeVehicle={removeVehicle}
                    columnName={name}
                  />
                  <ChangeButton
                    styleId={styleId}
                    toggleModal={toggleModal}
                    columnName={name}
                    makeName={get(styles, `[${index}].makeName`)}
                    modelName={get(styles, `[${index}].modelName`)}
                    year={get(styles, `[${index}].year`)}
                  />
                </div>
              ) : (
                <AddButton toggleModal={toggleModal} columnName={name} disabled={isColumnDisabled} />
              );
            }),
          },
        ],
      },
    ];

    const stickyCarHeaderRenderMap = [
      {
        ...carHeaderRenderMap[0],
        ref: null,
        isThead: false,
      },
    ];

    const renderMap = [
      ...carHeaderRenderMap,
      {
        alternateFeaturesBgColor: false,
        features: [
          {
            rowLabel: 'Photo',
            cellClassName: 'px-0',
            data: columns.map(
              ({ styleId, oemLogoSrc, oemLogoLink }, index) =>
                !!styleId && (
                  <AspectRatioContainer aspectRatio="3:2">
                    <CarImage
                      makeSlug={get(styles, `[${index}].makeSlug`)}
                      modelSlug={get(styles, `[${index}].modelSlug`)}
                      submodelName={get(styles, `[${index}].submodels.name`)}
                      submodelSlug={get(styles, `[${index}].submodels.slug`)}
                      trimName={get(styles, `[${index}].trimName`) || get(styles, `[${index}].trim`)}
                      year={get(styles, `[${index}].year`)}
                      sponsoredLogo={oemLogoSrc}
                      sponsoredLogoLink={oemLogoLink}
                    />
                    <TypeRankingsLink
                      styleId={styleId}
                      makeSlug={get(styles, `[${index}].makeSlug`)}
                      modelSlug={get(styles, `[${index}].modelSlug`)}
                      submodelSlug={get(styles, `[${index}].submodels.slug`)}
                      year={get(styles, `[${index}].year`)}
                    />
                  </AspectRatioContainer>
                )
            ),
          },
          {
            cellClassName: classnames({ 'text-center': isSmall }),
            rowLabel: 'Find inventory',
            data: columns.map(({ styleId }, index) => {
              if (!styleId) {
                return null;
              }

              const srpButtonFragment = (
                <SrpButton
                  makeSlug={get(styles, `[${index}].makeSlug`)}
                  modelSlug={get(styles, `[${index}].modelSlug`)}
                  year={get(styles, `[${index}].year`)}
                  publicationState={get(styles, `[${index}].publicationState`)}
                  makeName={get(styles, `[${index}].makeName`)}
                  modelName={get(styles, `[${index}].modelName`)}
                />
              );

              // TODO: Remove isUsed check after core-6982
              const isUsedPubState = isUsed(get(styles, `[${index}].publicationState`, ''));

              return isUsedPubState ? (
                srpButtonFragment
              ) : (
                <Experiment name="core-6982-comparator-leads" showDefault>
                  <Recipe name="ctrl" isDefault>
                    {srpButtonFragment}
                  </Recipe>
                  <Recipe name="chal1">
                    <LeadFormButton
                      style={styles?.[index]}
                      bestDealPrice={pricing?.[index]?.tmv}
                      showLeadForm={!!dealerCount?.[index]}
                      fallbackFragment={srpButtonFragment}
                      isMobile={isSmall}
                    />
                  </Recipe>
                  <Recipe name="chal2">{srpButtonFragment}</Recipe>
                </Experiment>
              );
            }),
          },
        ],
      },
      // end heading
      {
        heading: 'Pricing',
        features: [
          {
            label: 'Starting MSRP',
            data: columns.map(
              ({ styleId }, index) =>
                !!styleId &&
                withHash(
                  <Fragment>{getDisplayPriceValue(get(pricing, `[${index}].baseMSRP`))}</Fragment>,
                  get(pricing, `[${index}].baseMSRP`)
                )
            ),
          },
          {
            label: 'Average Price Paid',
            data: columns.map(
              ({ styleId }, index) =>
                !!styleId &&
                withHash(
                  <Fragment>{getDisplayPriceValue(get(pricing, `[${index}].tmv`))}</Fragment>,
                  get(pricing, `[${index}].tmv`)
                )
            ),
          },
          {
            label: 'Invoice',
            data: columns.map(
              ({ styleId }, index) =>
                !!styleId &&
                withHash(
                  <Fragment>{getDisplayPriceValue(get(pricing, `[${index}].baseInvoice`))}</Fragment>,
                  get(pricing, `[${index}].baseInvoice`)
                )
            ),
          },
          {
            label: 'Federal Tax Credit',
            data: columns.map(({ styleId }, index) => {
              const evTaxCreditAmount = get(evTaxCredit, index);
              const dealsUrl =
                !!get(styles, `[${index}].makeSlug`) &&
                getDealsUrl(
                  {
                    makeSlug: get(styles, `[${index}].makeSlug`),
                    modelSlug: get(styles, `[${index}].modelSlug`),
                    year: get(styles, `[${index}].year`),
                  },
                  { styleId, section: INCENTIVE_TYPES.OTHER }
                );

              return (
                !!styleId &&
                !!dealsUrl &&
                hasEvSection &&
                (evTaxCreditAmount ? (
                  <Fragment>
                    Up to {getPriceString(evTaxCreditAmount)}{' '}
                    <Link
                      to={dealsUrl}
                      className="text-primary-darker text-underline"
                      data-tracking-id="view_ev_tax_credit"
                    >
                      Based on income
                    </Link>
                  </Fragment>
                ) : (
                  withHash(<Fragment>Not Eligible</Fragment>, 'not-eligible')
                ))
              );
            }),
          },
          {
            excludeAlternateFeaturesBgColor: true,
            cellClassName: classnames({ 'text-center': isSmall }),
            rowLabel: 'Full vehicle details', // TODO: Review after core-6982
            data: columns.map(({ styleId }, index) => {
              if (!styleId) {
                return null;
              }

              const coreButtonFragment = (
                <CoreButton
                  makeSlug={get(styles, `[${index}].makeSlug`)}
                  modelSlug={get(styles, `[${index}].modelSlug`)}
                  submodelSlug={get(styles, `[${index}].submodels.slug`)}
                  year={get(styles, `[${index}].year`)}
                  publicationState={get(styles, `[${index}].publicationState`)}
                  makeName={get(styles, `[${index}].makeName`)}
                  modelName={get(styles, `[${index}].modelName`)}
                />
              );

              // TODO: Remove isUsed check after core-6982
              const isUsedPubState = isUsed(get(styles, `[${index}].publicationState`, ''));

              return isUsedPubState ? (
                coreButtonFragment
              ) : (
                <Experiment name="core-6982-comparator-leads" showDefault>
                  <Recipe name="ctrl" isDefault>
                    {coreButtonFragment}
                  </Recipe>
                  <Recipe name="chal1">{coreButtonFragment}</Recipe>
                  <Recipe name="chal2">
                    <LeadFormButton
                      style={styles?.[index]}
                      bestDealPrice={pricing?.[index]?.tmv}
                      showLeadForm={!!dealerCount?.[index]}
                      btnColor="outline-primary-b"
                      fallbackFragment={coreButtonFragment}
                      isMobile={isSmall}
                    />
                  </Recipe>
                </Experiment>
              );
            }),
          },
          {
            excludeAlternateFeaturesBgColor: true,
            cellClassName: 'px-0',
            rowLabel: 'Offers',
            isDisplayFlex: ads11245Recipe === 'chal2',
            data: columns.map(
              ({ styleId }, index) =>
                !!styleId && (
                  <Experiment name="ads-11245" showDefault>
                    <Recipe name="ctrl" isDefault>
                      <ComparatorIncentivesAd
                        mobile={isSmall}
                        wrapperClass="px-0 mt-2"
                        style={get(styles, index)}
                        position={`${index}`}
                      />
                    </Recipe>
                    <Recipe name="chal1">
                      <ComparatorIncentivesAd
                        mobile={isSmall}
                        wrapperClass="px-0 mt-2"
                        style={get(styles, index)}
                        position={`${index}`}
                        ads11245Enabled
                      />
                    </Recipe>
                    <Recipe name="chal2">
                      <ComparatorIncentivesAd
                        mobile={isSmall}
                        wrapperClass="px-0 mt-2"
                        style={get(styles, index)}
                        position={`${index}`}
                        ads11245Enabled
                      />
                    </Recipe>
                  </Experiment>
                )
            ),
          },
        ],
      },
      {
        alternateFeaturesBgColor: false,
        heading: 'Edmunds Rating',
        features: [
          {
            label: 'Overall Rating',
            data: columns.map(
              ({ styleId }, index) =>
                !!styleId &&
                withHash(
                  <OverallRating
                    rating={getOverallRating(editorialReview?.[index])}
                    ratingScale={getRatingScale(get(editorialReview, `[${index}]`))}
                    showStars={false}
                    ratingScaleClassName={classnames({
                      'heading-4 mt-1 mb-0_5': !isSmall,
                      'heading-5': isSmall,
                    })}
                    showLogoDesign
                  />,
                  get(editorialReview, `[${index}].id`)
                )
            ),
          },
          {
            rowLabel: 'Rating breakdown',
            data: columns.map(
              ({ styleId }, index) =>
                !!styleId &&
                withHash(
                  <EditorialReviewRating
                    ratings={get(editorialReview, `[${index}].ratings`)}
                    isScorecardV4={get(editorialReview, `[${index}].isScorecardV4`)}
                    params={get(comparableFeatures, `[${index}].params`)}
                  />,
                  get(editorialReview, `[${index}].id`)
                )
            ),
          },
          {
            rowLabel: 'Full review',
            cellClassName: classnames({ 'text-center': isSmall }),
            data: columns.map(
              ({ styleId }, index) =>
                !!styleId && (
                  <EdmundsRatingButton
                    className={classnames('px-1 py-0_5', {
                      'size-16': !isSmall,
                    })}
                    makeSlug={get(styles, `[${index}].makeSlug`)}
                    modelSlug={get(styles, `[${index}].modelSlug`)}
                    year={get(styles, `[${index}].year`)}
                    toReviewPage={isUsed(get(styles, `[${index}].publicationState`, ''))}
                    trackingId="mmy_select_submit_mmyt"
                    makeName={get(styles, `[${index}].makeName`)}
                    modelName={get(styles, `[${index}].modelName`)}
                  />
                )
            ),
          },
        ],
      },
      {
        alternateFeaturesBgColor: false,
        heading: 'Consumer Rating',
        features: [
          {
            label: 'Overall consumer rating',
            data: columns.map(
              ({ styleId }, index) =>
                !!styleId &&
                withHash(
                  <OverallRating
                    rating={get(consumerRating, `[${index}].ratingAggregation.averageStars`)}
                    ratingScaleClassName={classnames({
                      'mt-1 heading-4': !isSmall,
                      'heading-5': isSmall,
                    })}
                    ratingStarClassName={classnames({
                      medium: isSmall,
                    })}
                  />,
                  `${get(consumerRating, `[${index}].ratingAggregation.averageStars`)}-${get(
                    consumerRating,
                    `[${index}].totalReviews`
                  )}`
                )
            ),
          },
          {
            rowLabel: 'Detailed consumer rating',
            data: columns.map(({ styleId }, index) => {
              const { totalReviews, ratingAggregation } = get(consumerRating, `[${index}]`) || {};
              return (
                !!styleId &&
                withHash(
                  <div className={classnames({ 'consumer-rating-disabled': !totalReviews })}>
                    <RatingLines total={totalReviews} ratings={ratingAggregation} isAvailable={!!totalReviews} />
                  </div>,
                  `${get(consumerRating, `[${index}].ratingAggregation.averageStars`)}-${get(
                    consumerRating,
                    `[${index}].totalReviews`
                  )}`
                )
              );
            }),
          },
          {
            rowLabel: 'Read consumer reviews',
            cellClassName: classnames({ 'text-center': isSmall }),
            data: columns.map(
              ({ styleId }, index) =>
                !!styleId && (
                  <ConsumerRatingButton
                    total={get(consumerRating, `[${index}].totalReviews`)}
                    makeSlug={get(styles, `[${index}].makeSlug`)}
                    modelSlug={get(styles, `[${index}].modelSlug`)}
                    year={get(styles, `[${index}].year`)}
                    className={classnames({ 'size-16': !isSmall })}
                    trackingId="mmy_select_submit_mmyt"
                    trackingValue="Read consumer reviews"
                    makeName={get(styles, `[${index}].makeName`)}
                    modelName={get(styles, `[${index}].modelName`)}
                  />
                )
            ),
          },
        ],
      },
      ...featuresMap
        .filter(({ isEvSection }) => !isEvSection || hasEvSection)
        .map(({ values, camelName: categorySlug, name: categoryName }) => ({
          heading: categoryName,
          features: values.map(value => ({
            label: getFeatureColumns({
              evComparableFeatures,
              columns,
              feature: value,
            }).map(({ feature: { name, displayName } }) => displayName || name),
            data: getFeatureColumns({ evComparableFeatures, columns, feature: value }).map(
              (
                {
                  column,
                  feature: {
                    name,
                    displayName,
                    camelName: nameSlug,
                    categoryCamelName: customCategorySlug,
                    forceValue,
                    blank,
                    optional,
                    isEvData,
                  },
                },
                index
              ) => {
                let featureValue = isEvData
                  ? get(evComparableFeatures, `[${index}][${nameSlug}]`, NOT_AVAILABLE)
                  : get(comparableFeatures, `[${index}][${categoryName}][${name}]`, '');

                if (!nameSlug) {
                  featureValue = `<span class="medium">Not applicable</span>`;
                }

                if (forceValue) {
                  featureValue = forceValue;
                }

                const noValue = blank || (!hasFeatureValue(featureValue) && optional);

                return (
                  !!column.styleId &&
                  !noValue &&
                  withHash(
                    <FeatureField
                      value={numberWithCommasForString(featureValue)}
                      featureCategorySlug={customCategorySlug || categorySlug}
                      featureNameSlug={nameSlug}
                      name={displayName || name}
                      style={column}
                      addVehicle={addVehicle}
                      buttonClass={classnames({ 'size-16': !isSmall })}
                    />,
                    featureValue === AVAILABLE_ON_OTHER_STYLES
                      ? ''
                      : `${name}-${nameSlug ? featureValue : 'not-applicable'}`
                  )
                );
              }
            ),
            blank: getFeatureColumns({ evComparableFeatures, columns, feature: value }).map(
              ({ column, feature: { blank } }) => !!(column.styleId && blank)
            ),
          })),
        })),
      {
        heading: '5-Year Ownership Costs',
        features: map(tcoMap, (name, niceName) => ({
          label: name,
          data: styles.map(
            (style, index) =>
              !!get(style, 'id') &&
              withHash(
                <Fragment>
                  {typeof get(tco, `[${index}][${niceName}]`) === 'number'
                    ? formatPriceString(get(tco, `[${index}][${niceName}]`), 0, 2)
                    : NOT_AVAILABLE}
                </Fragment>,
                get(tco, `[${index}][${niceName}]`)
              )
          ),
        })),
      },
      {
        alternateFeaturesBgColor: false,
        heading: 'Colors',
        features: [
          {
            label: 'Interior',
            data: columns.map(
              ({ styleId, name }, index) =>
                !!styleId &&
                withHash(
                  <CarColorSection
                    idPrefix={name}
                    type="interior"
                    colors={get(colors, `[${index}].INTERIOR`)}
                    isSmall={isSmall}
                  />,
                  [
                    'interior',
                    get(colors, `[${index}].INTERIOR`)?.map(({ color }) => `${color?.r},${color?.g},${color?.b}`),
                  ].join()
                )
            ),
          },
          {
            label: 'Exterior',
            data: columns.map(
              ({ styleId, name }, index) =>
                !!styleId &&
                withHash(
                  <CarColorSection
                    idPrefix={name}
                    type="exterior"
                    colors={get(colors, `[${index}].EXTERIOR`)}
                    isSmall={isSmall}
                  />,
                  [
                    'exterior',
                    get(colors, `[${index}].EXTERIOR`)?.map(({ color }) => `${color?.r},${color?.g},${color?.b}`),
                  ].join()
                )
            ),
          },
        ],
      },
    ];

    return {
      stickyCarHeaderRenderMap,
      renderMap: shouldRenderSelected ? [...carHeaderTitleRenderMap, ...renderMap] : carHeaderRenderMap,
    };
  }, [
    addVehicle,
    colors,
    columns,
    comparableFeatures,
    comparatorHeaderRef,
    consumerRating,
    editorialReview,
    evComparableFeatures,
    evTaxCredit,
    isSmall,
    pricing,
    removeVehicle,
    shouldRenderSelected,
    styles,
    tco,
    toggleModal,
  ]);

  if (!comparableFeatures) {
    return null;
  }

  return (
    <div className="comparator-table">
      <ContentTable
        renderMap={contentData.renderMap}
        keyPrefix="features"
        navSections={SUB_NAV_SECTIONS_MAP}
        isSmall={isSmall}
        noBorders={!shouldRenderSelected}
        hideSimilar={hideSimilar}
        enableAds10208={enableAds10208}
      />
      <AnimatedSticky
        className="heading-row"
        onStateChange={onStickyStateChange}
        intersectionRef={comparatorHeaderRef}
        stickyClassName="px-0"
        stickyFragment={
          <div className="sticky-header-wrapper overflow-x-hidden px-0_5" ref={comparatorHeaderStickyRef} aria-hidden>
            {!isSmall && (
              <Fragment>
                <div className="heading-2 pt-1 px-xl-0_25 mb-0 text-start">{title}</div>
                {hasComparisonContent && (
                  <AnchorSubNavigation
                    customOffset={customOffset}
                    list={navList}
                    hasBubblesDesign
                    idName="subnav-animated"
                  />
                )}
              </Fragment>
            )}
            <ContentTable
              renderMap={contentData.stickyCarHeaderRenderMap}
              keyPrefix="sticky-header"
              isSmall={isSmall}
              hideSimilar={hideSimilar}
              noBorders
            />
          </div>
        }
      />
      {modalStyleId ? <StyleDataWrapper styleId={modalStyleId}>{mmysModal}</StyleDataWrapper> : mmysModal}
    </div>
  );
}

ComparatorTableUI.propTypes = {
  title: PropTypes.string.isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      styleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ).isRequired,
  navList: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, hash: PropTypes.string })).isRequired,
  addVehicle: PropTypes.func.isRequired,
  removeVehicle: PropTypes.func.isRequired,
  onStickyStateChange: PropTypes.func.isRequired,
  styles: PropTypes.arrayOf(VehicleEntities.Style),
  comparableFeatures: PropTypes.arrayOf(
    PropTypes.shape({
      exteriorFeatures: PropTypes.objectOf(PropTypes.string),
      interiorFeatures: PropTypes.objectOf(PropTypes.string),
      safety: PropTypes.objectOf(PropTypes.string),
      specifications: PropTypes.objectOf(PropTypes.string),
      warranty: PropTypes.objectOf(PropTypes.string),
    })
  ),
  evComparableFeatures: PropTypes.arrayOf(PropTypes.object),
  evTaxCredit: PropTypes.arrayOf(PropTypes.number),
  pricing: PropTypes.arrayOf(
    PropTypes.shape({
      tmv: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      baseMSRP: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      baseInvoice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    })
  ),
  dealerCount: PropTypes.arrayOf(PropTypes.number),
  editorialReview: PropTypes.arrayOf(EditorialReviewEntities.EditorialReview),
  consumerRating: PropTypes.arrayOf(ConsumerReviewsEntities.ConsumerReviewsRatingsCount),
  tco: PropTypes.arrayOf(
    PropTypes.shape({
      federalTaxCredit: PropTypes.number,
      insurance: PropTypes.number,
      maintenance: PropTypes.number,
      repairs: PropTypes.number,
      taxesAndFees: PropTypes.number,
      financeInterest: PropTypes.number,
      depreciation: PropTypes.number,
      fuel: PropTypes.number,
      tcoPrice: PropTypes.number,
      averageCostPerMile: PropTypes.number,
    })
  ),
  colors: PropTypes.arrayOf(
    PropTypes.shape({
      EXTERIOR: PropTypes.arrayOf(
        PropTypes.shape({
          title: PropTypes.string,
          color: PropTypes.shape({
            r: PropTypes.number,
            g: PropTypes.number,
            b: PropTypes.number,
          }),
        })
      ),
      INTERIOR: PropTypes.arrayOf(
        PropTypes.shape({
          title: PropTypes.string,
          color: PropTypes.shape({
            r: PropTypes.number,
            g: PropTypes.number,
            b: PropTypes.number,
          }),
        })
      ),
    })
  ),
  sponsoredContent: PropTypes.shape({
    oemConsumerReviewLink: PropTypes.string,
    oemEdmundsReviewLink: PropTypes.string,
  }),
  customOffset: PropTypes.number,
  comparatorHeaderRef: PropTypes.shape(),
  comparatorHeaderStickyRef: PropTypes.shape(),
  shouldRenderSelected: PropTypes.bool,
  isSmall: PropTypes.bool,
  hasComparisonContent: PropTypes.bool,
  hideSimilar: PropTypes.bool,
  enableAds10208: PropTypes.bool,
  ads11245Recipe: PropTypes.string,
};

ComparatorTableUI.defaultProps = {
  comparableFeatures: null,
  evComparableFeatures: null,
  evTaxCredit: null,
  styles: [],
  pricing: null,
  dealerCount: null,
  editorialReview: null,
  consumerRating: null,
  tco: null,
  colors: null,
  columns: [],
  sponsoredContent: null,
  comparatorHeaderRef: null,
  comparatorHeaderStickyRef: null,
  shouldRenderSelected: false,
  customOffset: null,
  isSmall: false,
  hasComparisonContent: true,
  hideSimilar: false,
  enableAds10208: false,
  ads11245Recipe: 'ctrl',
};

export const propsAreEqual = (prevProps, { comparableFeatures, dealerCount, isCore6982Enabled }) =>
  !comparableFeatures || !!(!dealerCount && isCore6982Enabled);
const mapStateToProps = state => ({
  ads11245Recipe: ExperimentUtil.getForcedOrAssignedRecipeName({
    state,
    campaignName: 'ads-11245',
    defaultVal: 'ctrl',
  }),
});

export const ComparatorTable = connect(mapStateToProps)(
  connectToModel(React.memo(ComparatorTableUI, propsAreEqual), {
    comparableFeatures: bindToPath(({ columns }) => {
      if (!columns.length) {
        return null;
      }
      return `comparableFeatures["${getColumnIdsString(columns)}"]`;
    }, ComparatorModel),
    evComparableFeatures: bindToPath(({ columns }) => {
      if (!columns.length) {
        return null;
      }
      return `evEfficiency["${getColumnIdsString(columns)}"]`;
    }, ComparatorModel),
    evTaxCredit: bindToPath(({ columns }) => {
      if (!columns.length) {
        return null;
      }
      return `evTaxCredit["${getColumnIdsString(columns)}"]`;
    }, ComparatorModel),
    pricing: bindToPath(({ columns }) => {
      if (!columns.length) {
        return null;
      }
      return `pricing["${getColumnIdsString(columns)}"]`;
    }, ComparatorModel),
    dealerCount: bindToPath(({ columns, isCore6982Enabled }) => {
      if (!columns.length || !isCore6982Enabled) {
        return null;
      }
      return `dealerCount["${getColumnIdsString(columns)}"]`;
    }, ComparatorModel),
    editorialReview: bindToPath(({ columns }) => {
      if (!columns.length) {
        return null;
      }
      return `editorialReview["${getColumnIdsString(columns)}"]`;
    }, ComparatorModel),
    consumerRating: bindToPath(({ columns }) => {
      if (!columns.length) {
        return null;
      }
      return `consumerRating["${getColumnIdsString(columns)}"]`;
    }, ComparatorModel),
    tco: bindToPath(({ styles }) => {
      if (styles.every(style => !style)) {
        return null;
      }
      return `tco["${getTCOIdsString(styles)}"]`;
    }, ComparatorModel),
    colors: bindToPath(({ columns }) => {
      if (!columns.length) {
        return null;
      }
      return `colors["${getColumnIdsString(columns)}"]`;
    }, ComparatorModel),
  })
);
